import { useState } from "react";
import { AlertCircle, MoreHorizontal } from "lucide-react";

import AttachmentPreview from "@/components/communications/AttachmentPreview";
import { fmtExactZoned, fmtMailDate, formatDeliveryError, OUTBOUND_STATUS, splitQuotedEmail, TELEGRAM_SENT_NOTE } from "@/components/communications/threadFormat";
import { ButtonShadcn as Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";

const initials = (name) =>
  (name || "?")
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");

const FAILED = ["failed", "bounced"];

// Where an outbound email got to, as words on the page's own surface. The
// chat bubble's tick is drawn for a coloured bubble and all but vanishes here.
const DeliveryStatus = ({ item }) => {
  const meta = OUTBOUND_STATUS[item.status] || OUTBOUND_STATUS.sent;
  const Icon = meta.icon;
  const failed = FAILED.includes(item.status);
  const reason = formatDeliveryError(item.last_error) || (item.channel === "telegram" && item.status === "sent" ? TELEGRAM_SENT_NOTE : "");
  return (
    <span className={cn("inline-flex items-center gap-1", failed ? "text-tone-rose" : "text-text-secondary")} title={reason || undefined}>
      <Icon size={13} aria-hidden="true" />
      {meta.label}
    </span>
  );
};

/**
 * One message in the reading pane, headed the way a mail client heads it:
 * who sent it, who it went to, when. Folded, it is one line — sender, first
 * words, date — and a click opens it, the way Gmail folds the older messages
 * of a long thread so the newest one is what the eye lands on.
 *
 * The history a candidate's mail client quotes under their reply is folded
 * behind "Show quoted text"; the stored text stays whole.
 *
 * Outbound is "You" for the same reason a quoted outbound message is: every
 * message sent from this tab was sent by the team reading it.
 */
const EmailMessage = ({ item, contactName, expanded, onToggle }) => {
  const [showQuoted, setShowQuoted] = useState(false);
  const outbound = item.direction === "outbound";
  const sender = outbound ? "You" : contactName || item.from_email || "Candidate";
  const address = outbound ? null : item.from_email;
  const failed = outbound && FAILED.includes(item.status);
  const when = fmtMailDate(item.timestamp);
  const exact = fmtExactZoned(item.timestamp);
  const { body, quoted } = splitQuotedEmail(item.text);

  if (!expanded) {
    return (
      <article aria-label={`${sender}, ${exact}`}>
        <Button
          variant="ghost"
          aria-expanded="false"
          onClick={onToggle}
          className="h-auto w-full justify-start gap-3 rounded-none px-4 py-3 text-left font-normal hover:bg-background-primary"
        >
          <span className="w-32 shrink-0 truncate text-sm font-medium text-text-primary">{sender}</span>
          <span className="min-w-0 flex-1 truncate text-sm text-text-secondary">{body || "(no content)"}</span>
          {failed && <AlertCircle size={14} aria-label="Not delivered" className="shrink-0 text-tone-rose" />}
          <span className="shrink-0 text-xs text-text-secondary" title={exact}>
            {when}
          </span>
        </Button>
      </article>
    );
  }

  return (
    <article aria-label={`${sender}, ${exact}`} className="px-4 py-4">
      <div className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className={cn(
            "flex size-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
            outbound ? "bg-tone-blue-soft text-tone-blue" : "bg-background-tertiary text-text-primary",
          )}
        >
          {initials(outbound ? "You" : sender)}
        </span>
        <Button
          variant="ghost"
          aria-expanded="true"
          onClick={onToggle}
          className="h-auto min-w-0 flex-1 flex-col items-start gap-0 rounded p-0 text-left font-normal whitespace-normal hover:bg-transparent"
        >
          <span className="flex flex-wrap items-baseline gap-x-1.5">
            <span className="text-sm font-semibold text-text-primary">{sender}</span>
            {address && <span className="truncate text-xs text-text-secondary">&lt;{address}&gt;</span>}
          </span>
          {outbound && item.to_email && <span className="block truncate text-xs text-text-secondary">{`to ${item.to_email}`}</span>}
          {!outbound && <span className="block text-xs text-text-secondary">to you</span>}
        </Button>
        <span className="flex shrink-0 flex-col items-end gap-0.5 text-xs text-text-secondary">
          <span title={exact}>{when}</span>
          {outbound && <DeliveryStatus item={item} />}
        </span>
      </div>

      <div className="mt-3 pl-12 text-sm leading-relaxed whitespace-pre-wrap break-words text-text-primary">{body || <span className="italic text-text-secondary">(no content)</span>}</div>

      {quoted && (
        <div className="mt-2 pl-12">
          <Button
            type="button"
            variant="tertiary"
            size="icon-xs"
            aria-label={showQuoted ? "Hide quoted text" : "Show quoted text"}
            aria-expanded={showQuoted}
            title={showQuoted ? "Hide quoted text" : "Show quoted text"}
            onClick={() => setShowQuoted((v) => !v)}
          >
            <MoreHorizontal aria-hidden="true" />
          </Button>
          {showQuoted && <div className="mt-2 border-l-2 border-border-primary pl-3 text-sm leading-relaxed whitespace-pre-wrap break-words text-text-secondary">{quoted}</div>}
        </div>
      )}

      {item.attachment && (
        <div className="mt-3 pl-12">
          <AttachmentPreview attachment={item.attachment} outbound={false} />
        </div>
      )}

      {failed && (
        <p role="alert" className="mt-3 ml-12 flex items-start gap-1.5 rounded-md bg-tone-rose-soft px-2.5 py-2 text-xs text-text-primary">
          <AlertCircle size={14} aria-hidden="true" className="mt-px shrink-0 text-tone-rose" />
          <span>
            <span className="font-semibold">Not delivered.</span> {formatDeliveryError(item.last_error) || "The mail provider did not accept this message."}
          </span>
        </p>
      )}
    </article>
  );
};

export default EmailMessage;
